import { Component, EventEmitter, forwardRef, Inject, Input, Optional, Output, SimpleChanges, ViewChild } from "@angular/core";
import { VFlvPlayer } from "@app/directives/_misc/flvPlayer/flv-player.component";
import { VStreamCollectionComponent } from "@app/directives/_veri/veriVideo/v-stream-collection/v-stream-collection.component";
import { FlvPlayerMediaInfo, IStreamPlayer } from "@app/interfaces/IStreamPlayer";
import { IVideoStreamError } from "@app/interfaces/IVideoStreamError";
import { FtCloudStreamType, IVideoStream } from "@app/main/components/videos/models/ICameraChannel";
import { CameraService } from "@app/main/components/videos/services/camera.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, from, Subject } from "rxjs";
import { filter, switchMap } from "rxjs/operators";
import './v-vehicle-video-stream.component.scss';

@UntilDestroy()
@Component({
    selector: 'v-vehicle-video-stream',
    template: require('./v-vehicle-video-stream.component.html')
})
export class VVehicleVideoStreamComponent {
    private receivedMediaSub = new Subject<boolean>();
    private highQualitySub = new Subject<boolean>();

    @Input() streamingChannel: IVideoStream;
    @Input() streamTimeout: number = 1;
    @Input() showRestartOnTimeout: boolean = true;
    @Input() timeoutOnMouseIdle: boolean;

    @Output() cameraUnavailable = new EventEmitter<IVideoStream>();
    @Output() receivedMedia = new EventEmitter<{ mediaInfo: FlvPlayerMediaInfo }>();
    @Output() videoTimeout = new EventEmitter<void>();
    @Output() videoRestart = new EventEmitter<IVideoStream>();

    @ViewChild('flvPlayer') flvPlayer: VFlvPlayer;

    videoUrl: string;
    loading: boolean = true;
    message: string;
    errorMessage: string;
    showRestart: boolean;
    videoLoadSuccessful: boolean;

    networkTimeout: ReturnType<typeof setTimeout>;
    constructor(
        @Optional() @Inject(forwardRef(() => VStreamCollectionComponent)) private parent: VStreamCollectionComponent,
        private readonly translationService: TranslationService,
        private readonly cameraService: CameraService
    ) { }

    ngOnInit(): void {
        this.initStreamUpdatedSub();
        this.initHQToggleSub();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.setVideoUrl();
    }

    ngOnDestroy(): void {
        clearTimeout(this.networkTimeout);
    }

    onVideoError(event: { type: string, message: string, details: IVideoStreamError }): void {
        console.error(this.translationService.t("VehicleVideoStream.liveVideoError"), event.type, event.message, event.details);

        this.loading = false;

        if (event.details.code === 408) {
            this.errorMessage = this.translationService.t("vehicleVideoStream.cameraUnavailable");
        }
        else {
            this.errorMessage = this.translationService.t("vehicleVideoStream.networkTimeout");
            if (this.showRestartOnTimeout) {
                this.showRestart = true;
            }
        }
        this.cameraUnavailable.emit(this.streamingChannel);
    }

    onVideoReceivedMedia(event: { mediaInfo: FlvPlayerMediaInfo }): void {
        this.loading = false;
        this.videoLoadSuccessful = true;
        
        this.receivedMediaSub.next(true);
        this.receivedMedia.emit({ mediaInfo: event.mediaInfo });
    }

    onVideoTimeout(): void {
        console.error(this.translationService.t("VehicleVideoStream.liveVideoTimeout"));

        this.message = this.translationService.t("vehicleVideoStream.videoStopped");

        if (this.showRestartOnTimeout) {
            this.showRestart = true;
        }

        this.videoTimeout.emit();
    }

    onVideoPlaybackNotSupported(): void {
        console.error("VehicleVideoStream.videoPlaybackNotSupported");
        this.errorMessage = this.translationService.t('vehicleVideoStream.notSupported');
        this.loading = false;
        this.showRestart = false;
        this.cameraUnavailable.emit(this.streamingChannel);
    }

    restartVideo($event: MouseEvent): void {
        $event.stopPropagation();
        if (!this.flvPlayer.player) {
            return;
        }

        this.message = "";
        this.errorMessage = "";
        this.showRestart = false;
        this.loading = true;

        this.videoUrl = null;
        this.videoRestart.emit(this.streamingChannel);
    }

    toggleHighQuality(hq: boolean): void {
        this.highQualitySub.next(hq);
    }

    play(): Promise<void> {
        if (!this.flvPlayer.player) {
            return;
        }

        return this.flvPlayer.player.play();
    }

    pause(): void {
        if (!this.flvPlayer.player) {
            return;
        }

        this.flvPlayer.player.pause();
    }

    //--

    private setVideoUrl(): void {
        if (!this.streamingChannel) {
            return;
        }

        this.errorMessage = "";
        this.message = "";
        this.showRestart = false;

        this.videoUrl = this.streamingChannel.StreamUrl;

        this.networkTimeout = setTimeout(() => {
            if (!this.videoLoadSuccessful) {
                this.loading = false;
                this.errorMessage = this.translationService.t("vehicleVideoStream.networkTimeout");
                this.pause();
                this.cameraUnavailable.emit(this.streamingChannel);
            }
        }, 10_000)

    }

    private initStreamUpdatedSub(): void {
        this.parent.streamUpdated.pipe(untilDestroyed(this)).subscribe(stream => {
            if (stream.ChannelId !== this.streamingChannel.ChannelId) {
                return;
            }
            
            this.streamingChannel = stream;
            this.setVideoUrl();
        })
    }

    private initHQToggleSub(): void {
        combineLatest([this.highQualitySub, this.receivedMediaSub]).pipe(
            untilDestroyed(this),
            filter(([_, receivedMedia]) => receivedMedia && !this.errorMessage && !this.message),
            switchMap(([highQuality, _]) => {
                const streamType = highQuality
                    ? FtCloudStreamType.Mainstream
                    : FtCloudStreamType.Substream;

                const streamId = this.getCurrentStreamId();
                return from(this.cameraService.switchStreamType(streamId, streamType));
            })
        ).subscribe();
    }

    private getCurrentStreamId(): string {
        const streamUrl = this.streamingChannel.StreamUrl!;
        const urlBySlash = streamUrl.split('/');
        const lastSegment = urlBySlash[urlBySlash.length - 1];
        const lastSegmentByDot = lastSegment.split('.');

        return lastSegmentByDot[0];
    }

}
